import { ChapterResponse, assetsChaptersBulkAddUpdateCreate } from '@goldcast/api/content';
import { Clip, ClipMetadata } from '@goldcast/player-config/src/types/clip-types';
import { isSplitsInTimelineEnabledPure } from '@goldcast/player-config/src/utils/clipUtils';
import createStore from './store';
import { currentClip, saveClipChanges, updateClipCaptionsLoadingState, updateClipMetadataWithId } from './clip';
import { markCaptionsObsolete } from './captions';
import { sharedAPI } from './sharedAPI/sharedAPI';
import { addPendingUpdate, PendingEditType } from './pendingEditsStore';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { showErrorToast } from '@/libs/toast/toast';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';

type ClipState = Pick<
  Clip['asset_metadata'],
  | 'start'
  | 'end'
  | 'edits'
  | 'deletes'
  | 'merges'
  | 'magicLayout'
  | 'title'
  | 'subtitle'
  | 'layout'
  | 'captions'
  | 'size'
  | 'intro'
  | 'introDuration'
  | 'outro'
  | 'outroDuration'
  | 'font_location'
  | 'highlight_type'
  | 'word_highlight_color'
  | 'is_edited'
  | 'template_id'
  | 'caption_positions'
  | 'caption_styles'
  | 'hide_borders'
  | 'text_highlight_color'
  | 'scenes'
  | 'selected_lang'
  | 'fit'
  | 'visible_video_ids'
  | 'speaker_label_styles'
  | 'player_config'
>;

type ClipHistoryState = {
  clipData?: Partial<ClipState>;
  chapters?: ChapterResponse[];
  entityUpdated?: string;
};

interface ClipHistory {
  index: number;
  states: ClipHistoryState[];
}

export const clipHistory = createStore<Record<string, ClipHistory>>({});

const getCurrentChaptersForClip = (clipId: string): ChapterResponse[] | undefined => {
  return sharedAPI.getSnapshot()[clipId]?.transcriptStore?.chapterData;
};

const createClipHistoryState = (clipMetadata: ClipMetadata): ClipState => ({
  start: clipMetadata.start,
  end: clipMetadata.end,
  edits: clipMetadata.edits,
  deletes: clipMetadata.deletes,
  merges: clipMetadata.merges,
  magicLayout: clipMetadata.magicLayout,
  title: clipMetadata.title,
  subtitle: clipMetadata.subtitle,
  captions: clipMetadata.captions,
  size: clipMetadata.size,
  layout: clipMetadata.layout,
  intro: clipMetadata.intro,
  introDuration: clipMetadata.introDuration,
  outro: clipMetadata.outro,
  outroDuration: clipMetadata.outroDuration,
  font_location: clipMetadata.font_location,
  highlight_type: clipMetadata.highlight_type,
  word_highlight_color: clipMetadata.word_highlight_color,
  is_edited: clipMetadata.is_edited,
  template_id: clipMetadata.template_id,
  caption_positions: clipMetadata.caption_positions,
  caption_styles: clipMetadata.caption_styles,
  hide_borders: clipMetadata.hide_borders,
  text_highlight_color: clipMetadata.text_highlight_color,
  scenes: clipMetadata.scenes,
  selected_lang: clipMetadata.selected_lang,
  fit: clipMetadata.fit,
  visible_video_ids: clipMetadata.visible_video_ids,
  speaker_label_styles: clipMetadata.speaker_label_styles,
  player_config: clipMetadata.player_config
});

const replayChaptersForUndoRedo = ({
  clipId,
  broadcastId,
  chapters
}: {
  clipId: string;
  broadcastId?: string;
  chapters: ChapterResponse[];
}) => {
  EventBus.dispatch(CustomEvents.UpdateChapters, {
    chapterData: chapters,
    chaptersLastUpdatedAt: new Date().toISOString()
  });

  if (!broadcastId) {
    return;
  }

  const chapterUpdatePromise = assetsChaptersBulkAddUpdateCreate({
    body: chapters,
    queryParams: {
      content_id: broadcastId
    }
  });

  addPendingUpdate({
    clipId,
    updatePromise: chapterUpdatePromise,
    version: Date.now(),
    editType: PendingEditType.chapter
  });

  void chapterUpdatePromise.catch(error => {
    window.logger?.error('Failed to sync chapters for undo/redo', error);
    showErrorToast('Failed to sync chapters. Please try again.');
  });
};

const appendHistoryState = (clipId: string, state: ClipHistoryState) => {
  clipHistory.update((data: Record<string, ClipHistory>) => {
    const history = data[clipId];
    if (!history) {
      return {
        ...data,
        [clipId]: {
          index: 0,
          states: [state]
        }
      };
    }

    return {
      ...data,
      [clipId]: {
        ...history,
        states: [...history.states.slice(0, history.index + 1), state],
        index: history.index + 1
      }
    };
  });
};

const injectHistoryStateWithChapters = ({
  clipId,
  chapters,
  indexOffset
}: {
  clipId: string;
  chapters: ChapterResponse[] | undefined;
  indexOffset: number;
}) => {
  clipHistory.update(data => {
    const history = data[clipId];
    if (!history) {
      return data;
    }

    const targetIndex = history.index + indexOffset;
    if (targetIndex < 0 || targetIndex >= history.states.length) {
      return data;
    }

    const updatedStates = [...history.states];
    updatedStates[targetIndex] = {
      ...updatedStates[targetIndex],
      chapters: structuredClone(chapters)
    };

    return {
      ...data,
      [clipId]: {
        ...history,
        states: updatedStates
      }
    };
  });
};

export function initHistory(clipId: string, clipMetadata: ClipMetadata) {
  const chapters = getCurrentChaptersForClip(clipId);
  clipHistory.update(data => ({
    ...data,
    [clipId]: {
      index: 0,
      states: [
        {
          clipData: createClipHistoryState(clipMetadata),
          chapters: structuredClone(chapters)
        }
      ]
    } as ClipHistory
  }));
}

export function updateHistory(
  clipId: string,
  clipMetadata: ClipMetadata,
  chapters?: ChapterResponse[],
  entityUpdated?: string
) {
  appendHistoryState(clipId, {
    clipData: createClipHistoryState(clipMetadata),
    ...(chapters !== undefined ? { chapters: structuredClone(chapters) } : {}),
    ...(entityUpdated ? { entityUpdated } : {})
  });
}

export function appendChapterHistory(clipId: string, chapters: ChapterResponse[] | undefined) {
  appendHistoryState(clipId, { chapters: structuredClone(chapters) });
}

export function injectCurrentHistoryWithChapters(clipId: string, chapters: ChapterResponse[] | undefined) {
  injectHistoryStateWithChapters({ clipId, chapters, indexOffset: 0 });
}

/**
 * Chapters are fetched asynchronously after `initHistory` has already seeded the base
 * history state, so `states[0].chapters` is left undefined. Undo back to the base state then
 * finds no chapters to replay and does nothing. Once chapters load, write them into index 0
 * so undo can revert chapter edits to their initial value.
 */
export function injectInitialHistoryWithChapters(clipId: string, chapters: ChapterResponse[] | undefined) {
  clipHistory.update(data => {
    const history = data[clipId];
    if (!history || history.states.length === 0) {
      return data;
    }

    const updatedStates = [...history.states];
    updatedStates[0] = {
      ...updatedStates[0],
      chapters: structuredClone(chapters)
    };

    return {
      ...data,
      [clipId]: {
        ...history,
        states: updatedStates
      }
    };
  });
}

export function undoChange(clipId: string) {
  const history = clipHistory.getSnapshot()[clipId];
  if (!history || history.index <= 0) {
    return;
  }
  const undoneAction = history.states[history.index]?.entityUpdated;
  window.analytics?.track('ContentLabEvent', {
    name: 'Undo',
    clipId,
    undoneAction
  });
  const newHistoryIndex = history.index - 1;
  applyUndoRedo(clipId, history, newHistoryIndex, true);
}

export function redoChange(clipId: string) {
  const history = clipHistory.getSnapshot()[clipId];
  if (!history || history.index >= history.states.length - 1) {
    return;
  }
  const newHistoryIndex = history.index + 1;
  applyUndoRedo(clipId, history, newHistoryIndex);
}

function findLastDefinedState<K extends 'clipData' | 'chapters'>(
  states: ClipHistoryState[],
  fromIndex: number,
  key: K
): ClipHistoryState[K] | undefined {
  for (let index = fromIndex; index >= 0; index -= 1) {
    const value = states[index]?.[key];
    if (value !== undefined) {
      return value;
    }
  }
  return undefined;
}

function applyUndoRedo(clipId: string, _clipHistory: ClipHistory, newHistoryIndex: number, isUndo: boolean = false) {
  const currentClipValue = currentClip.getSnapshot()[clipId];
  const targetState = _clipHistory.states[newHistoryIndex];
  if (!currentClipValue || !targetState) {
    return;
  }

  const currentChange = _clipHistory.states[newHistoryIndex + 1];

  const currentAssetMetadata = currentClipValue.asset_metadata;
  const targetClipData = findLastDefinedState(_clipHistory.states, newHistoryIndex, 'clipData');
  const targetChapters = findLastDefinedState(_clipHistory.states, newHistoryIndex, 'chapters');
  const shouldUpdateClipState = isUndo ? currentChange?.clipData && targetClipData : targetClipData;

  if (shouldUpdateClipState) {
    markCaptionsObsolete({ clipId });
    updateClipCaptionsLoadingState({ clipId, loading: true });

    const finalUpdateValue = { ...currentAssetMetadata, ...targetClipData };
    const captionsPositions = finalUpdateValue.caption_positions;
    if (captionsPositions) {
      const { x, y, width, height } = captionsPositions;

      EventBus.dispatch(CustomEvents.UpdateCaptionsPosition, {
        left: x,
        top: y,
        width,
        height
      });
    }

    updateClipMetadataWithId(clipId, finalUpdateValue, false);

    const hadSplits = isSplitsInTimelineEnabledPure({
      clip: currentClipValue,
      isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
    });
    const newHasSplits = isSplitsInTimelineEnabledPure({
      clip: currentClip.getSnapshot()[clipId],
      isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
    });

    if (hadSplits !== newHasSplits) {
      window.analytics?.track('ContentLabEvent', {
        name: 'UndoRedoSplitsChanged',
        isSuccess: false,
        clipId,
        addedSplits: newHasSplits,
        removedSplits: hadSplits,
        action: isUndo ? 'Undo' : 'Redo'
      });
    }
  }

  const shouldUpdateChapters = targetChapters && (!isUndo || currentChange?.chapters);
  if (shouldUpdateChapters) {
    replayChaptersForUndoRedo({
      clipId,
      broadcastId: currentClipValue.broadcast,
      chapters: targetChapters
    });
  }

  clipHistory.update(data => ({ ...data, [clipId]: { ...data[clipId], index: newHistoryIndex } }));

  if (shouldUpdateClipState) {
    saveClipChanges({
      clipId,
      shouldGenerateThumbnail: false,
      shouldAddToHistory: false,
      isEditedState: targetClipData?.is_edited ?? currentAssetMetadata.is_edited
    });
  }
}
